import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Car, Truck, Package } from "lucide-react";
import { api } from "@/lib/api";
import CarCard from "@/components/CarCard";
import SearchPanel from "@/components/SearchPanel";
import useDocumentMeta from "@/lib/useDocumentMeta";

// Category definitions: which body_style values belong to each tab
const CATEGORIES = [
    {
        key: "all",
        label: "Всички",
        icon: null,
        bodies: null, // null = no filter
    },
    {
        key: "cars",
        label: "Леки",
        icon: Car,
        bodies: ["Sedan", "Coupe", "SUV", "Hatchback", "Wagon", "Convertible"],
    },
    {
        key: "vans",
        label: "Бусове / Микробуси",
        icon: Truck,
        bodies: ["Van"],
    },
    {
        key: "trucks",
        label: "Камиони",
        icon: Package,
        bodies: ["Pickup", "Truck"],
    },
];

export default function CarsPage() {
    const [params, setParams] = useSearchParams();
    const [allCars, setAllCars] = useState([]);
    const [loading, setLoading] = useState(true);

    useDocumentMeta({
        title: "Автомобили — Konstantin Mobile | Цялата налична селекция",
        description:
            "Разгледайте всички налични автомобили в Konstantin Mobile, Петрич. Mercedes, BMW, Audi, Porsche, VW и още — с филтри по марка, гориво, скоростна кутия и категория.",
        path: "/cars",
    });

    // Fetch ALL cars once with current query params (including new `category` filter)
    useEffect(() => {
        const q = {};
        params.forEach((v, k) => {
            if (v !== "") q[k] = v;
        });
        setLoading(true);
        api.get("/cars", { params: { ...q, limit: 200 } })
            .then((r) => setAllCars(r.data || []))
            .finally(() => setLoading(false));
    }, [params]);

    // Server-side filter already applied (category, brand, fuel, etc.) — just render what API returned.
    const visibleCars = allCars;
    const initial = Object.fromEntries(params.entries());

    return (
        <main data-testid="cars-page" className="font-konstantin relative pt-32 pb-24 min-h-screen bg-bg overflow-hidden">
            {/* KS logo watermark — large illustrative background */}
            <div
                aria-hidden="true"
                className="pointer-events-none fixed inset-0 flex items-start justify-center opacity-[0.10] select-none -z-0"
                style={{
                    backgroundImage: "url(/ks-logo.png)",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center 30%",
                    backgroundSize: "min(80vw, 1100px) auto",
                }}
            />
            <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7 }}
                    className="mb-8"
                >
                    <span className="block text-gold text-[11px] uppercase tracking-[0.32em] mb-4">
                        Разгледай автомобилите
                    </span>
                    <h1
                        className="text-4xl lg:text-5xl font-light tracking-tight"
                        data-testid="cars-page-title"
                    >
                        Всички автомобили
                    </h1>
                    <p className="mt-3 text-ink-muted">
                        Намерени: <span className="text-gold">{visibleCars.length}</span>{" "}
                        {visibleCars.length === 1 ? "автомобил" : "автомобила"}
                    </p>
                </motion.div>

                <div className="mb-5">
                    <SearchPanel embedded initialFilters={initial} />
                </div>

                {loading ? (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                        {[...Array(6)].map((_, i) => (
                            <div
                                key={i}
                                className="aspect-[4/5] bg-surface rounded-2xl border border-line animate-pulse"
                            />
                        ))}
                    </div>
                ) : visibleCars.length === 0 ? (
                    <div className="py-24 text-center">
                        <p className="text-2xl font-display text-ink-muted">
                            Няма намерени автомобили в тази категория
                        </p>
                        <p className="text-ink-subtle mt-2">Опитайте с по-широки филтри или друг таб.</p>
                    </div>
                ) : (
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
                        {visibleCars.map((c, i) => (
                            <CarCard key={c.id} car={c} index={i} />
                        ))}
                    </div>
                )}
            </div>
        </main>
    );
}
